import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { useTheme } from '@famly/mf_theme';
import { Stack } from '@famly/mf_layout_stack';
import { Box } from '@famly/mf_layout_box';
import { Tooltip } from '@famly/mf_data-display_tooltip';
import { AvatarGroupContext } from './avatar-group.context';
import { getFontSize } from './avatar-get-font-size';
const OVERFLOW_CLASSNAME = 'MFAvatarGroup-overflow';
const DEFAULT_MAX = 4;
const DEFAULT_SIZE = 32;
const OVERLAP_PX = 8;
const BORDER_PX = 2;
/**
 * Computes how many avatars to show and the overflow count.
 *
 * Replicates the MUI AvatarGroup clamping logic so existing consumers
 * see identical behaviour with the same `max` / `children` combinations.
 *
 * @param totalChildren - total number of valid child elements
 * @param max - the configured `max` prop
 * @returns visibleCount and extraCount
 */
const computeOverflow = (totalChildren, max) => {
    let clampedMax = max < 2 ? 2 : max;
    // When the children count exactly equals max, show all without overflow
    if (totalChildren === clampedMax) {
        clampedMax += 1;
    }
    clampedMax = Math.min(totalChildren + 1, clampedMax);
    const visibleCount = Math.min(totalChildren, clampedMax - 1);
    const extraCount = Math.max(totalChildren - clampedMax, totalChildren - visibleCount, 0);
    return { visibleCount, extraCount };
};
export const AvatarGroup = ({ children, max = DEFAULT_MAX, size = DEFAULT_SIZE, overflowTooltipProps, ...props }) => {
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);
    const theme = useTheme();
    const validChildren = React.Children.toArray(children).filter(React.isValidElement);
    const { visibleCount, extraCount } = computeOverflow(validChildren.length, max);
    const visibleChildren = validChildren.slice(0, visibleCount);
    return (_jsx(AvatarGroupContext, { value: React.useMemo(() => ({ size }), [size]), children: _jsxs(Stack
        // Ensures that we start a new stacking context for the z-index applied to reverse the stacking order.
        , { 
            // Ensures that we start a new stacking context for the z-index applied to reverse the stacking order.
            position: "relative", justifyContent: "flex-end", ...dataProps, ...ariaProps, children: [visibleChildren.map((child, index) => {
                    var _a;
                    return (_jsx(Box, { width: `${size}px`, height: `${size}px`, borderRadius: "50%", boxSizing: "content-box", overflow: "visible", zIndex: index, ml: index === 0 ? 0 : `-${OVERLAP_PX}px`, style: {
                            border: `${BORDER_PX}px solid ${theme.colorPalette.n0}`,
                        }, children: child }, (_a = child.key) !== null && _a !== void 0 ? _a : index));
                }), extraCount > 0 ? (_jsx(OverflowIndicator, { extraCount: extraCount, size: size, visibleCount: visibleCount, overflowTooltipProps: overflowTooltipProps })) : null] }) }));
};
const OverflowIndicator = ({ extraCount, size, visibleCount, overflowTooltipProps }) => {
    const theme = useTheme();
    const indicator = (_jsx(Stack, { className: OVERFLOW_CLASSNAME, alignItems: "center", justifyContent: "center", width: `${size}px`, height: `${size}px`, borderRadius: "50%", backgroundColor: "b400", color: "n0", boxSizing: "content-box", ml: `-${OVERLAP_PX}px`, zIndex: visibleCount, cursor: overflowTooltipProps ? 'pointer' : undefined, style: {
            fontSize: `${getFontSize(size)}px`,
            lineHeight: 'unset',
            border: `${BORDER_PX}px solid ${theme.colorPalette.n0}`,
        }, children: `+${extraCount}` }));
    if (overflowTooltipProps) {
        return _jsx(Tooltip, { ...overflowTooltipProps, children: indicator });
    }
    return indicator;
};
